import React, { useState } from 'react';
import Dropzone from 'react-dropzone';
import styled from 'styled-components';
import HButton from '../components/common/HButton';
import * as xlsx from 'xlsx';
import Spinner from '../components/common/Spinner';

const Container = styled.div`
  display: flex;
  width: 100%;
  min-height: 100vh;
  min-width: 991px;
  align-items: center;
  flex-direction: column;
  background-color: transparent;
  padding-top: 60px;
  padding-bottom: 60px;
  box-sizing: border-box;
  overflow: hidden;

  .title {
    font-size: 36px;
    font-weight: 600;
    color: #59616a;
    margin-bottom: 40px;
  }
`;

const ContentBox = styled.div`
  display: flex;
  width: 100%;
  max-width: 800px;
  margin-top: 30px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const DownloadTemplate = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  margin-bottom: 20px;
`;

const RectangleBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  width: 100%;
  min-height: 150px;
  border-width: 2px;
  border-radius: 8px;
  border-color: #eeeeee;
  border-style: dashed;
  background-color: #fafafa;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: #f0f0f0;
  }

  p {
    font-size: 20px;
    color: #59616a;
  }
`;

const CheckBox = styled.div`
  display: flex;
  width: 100%;
  margin-top: 20px;
  align-items: center;
  justify-content: center;

  input[type='checkbox'] {
    margin-right: 10px;
    width: 18px;
    height: 18px;
  }

  label {
    font-size: 18px;
    color: #59616a;
    cursor: pointer;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  font-size: 18px;
  color: #59616a;
`;

const ResultBox = styled.div`
  margin-top: 40px;
  color: #28a745;
  font-size: 18px;
  text-align: center;
`;

const ErrorBox = styled.div`
  margin-top: 40px;
  color: #dc3545;
  font-size: 18px;
  text-align: center;
`;

const C4WRegistrationUpload = () => {
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const onClickDownloadTemplateExcel = () => {
    const token = localStorage.getItem('token');
    fetch('/heclore/v1/c4w/registrations/template/', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Error: ${response.statusText}`,
          );
        }
        return response.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'C4WRegistrationTemplate.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        setError(`Failed to download template file: ${error.message}`);
      });
  };

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    setError(null);
    setResult(null);
    setLoading(true);
  
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = xlsx.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        if (!sheetName) {
          setError('No sheets found in the Excel file.');
          setLoading(false);
          return;
        }
        const rows = xlsx.utils.sheet_to_json(workbook.Sheets[sheetName], {
          header: 1,
          blankrows: false,
        });
  
        if (!rows || rows.length < 2) {
          setError('The Excel file is empty or does not contain data rows.');
          setLoading(false);
          return;
        }
  
        const headerRow = rows[0].map(h => String(h).toLowerCase().trim());
        const udiIdx = headerRow.indexOf('udi');
        const featEnableIdx = headerRow.indexOf('feat_enable');
  
        if (udiIdx === -1) {
          setError('Invalid Excel format. The first column header must be "UDI".');
          setLoading(false);
          return;
        }
  
        const registrations = [];
        const seenUdis = new Set();
  
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i];
          if (!row || row.length === 0 || row[udiIdx] == null || String(row[udiIdx]).trim() === '') {
            continue;
          }
          const udiVal = String(row[udiIdx]).trim();
          if (seenUdis.has(udiVal)) {
            setError(`Duplicate UDI found in the uploaded file: ${udiVal}`);
            setLoading(false);
            return;
          }
          seenUdis.add(udiVal);
  
          const regObj = { udi: udiVal };
          if (featEnableIdx !== -1 && row[featEnableIdx] !== undefined && row[featEnableIdx] !== null && String(row[featEnableIdx]).trim() !== '') {
            // Convert to boolean if possible
            const val = String(row[featEnableIdx]).trim().toLowerCase();
            if (val === 'true' || val === '1' || val === 'yes') {
              regObj.feat_enable = true;
            } else if (val === 'false' || val === '0' || val === 'no') {
              regObj.feat_enable = false;
            } else {
              regObj.feat_enable = row[featEnableIdx];
            }
          }
          registrations.push(regObj);
        }
  
        if (registrations.length === 0) {
          setError('No valid UDIs found in the Excel file. Please ensure UDIs are listed under the "UDI" header.');
          setLoading(false);
          return;
        }
  
        // API Call to backend to add registrations
        const token = localStorage.getItem('token');
        fetch('/heclore/v1/c4w/registrations/upload/', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ registrations, overwrite: checked }),
        })
        .then(response => {
          if (!response.ok) {
            return response.json().then(errData => {
              throw new Error(errData.message || `API Error: ${response.statusText} (Status: ${response.status})`);
            }).catch(() => {
              throw new Error(`API Error: ${response.statusText} (Status: ${response.status})`);
            });
          }
          return response.json();
        })
        .then(apiResult => {
          let successMsg = '';
          let skippedMsg = '';
        
          // Calcule le nombre d'UDI enregistrés avec succès
          const skippedCount = apiResult.skipped ? apiResult.skipped.length : 0;
          const totalCount = registrations.length;
          const successCount = totalCount - skippedCount;
        
          if (successCount > 0) {
            successMsg = `Successfully uploaded ${successCount} registration(s).`;
          }
        
          if (apiResult.skipped && apiResult.skipped.length > 0) {
            skippedMsg =
              'Some registrations UDI are false:\n' +
              apiResult.skipped
                .map(item => {
                  let reason = item.reason;
                  if (reason === 'No change needed') {
                    reason = 'Registration already up to date';
                  }
                  return `UDI: ${item.udi} - ${reason}`;
                })
                .join('\n');
          }
        
          if (successMsg && skippedMsg) {
            setResult(`${successMsg}\n\n${skippedMsg}`);
          } else if (successMsg) {
            setResult(successMsg);
          } else if (skippedMsg) {
            setError(skippedMsg);
          }
        
          setLoading(false);
        })
        .catch(apiError => {
          setError(`Upload failed: ${apiError.message}`);
          setLoading(false);
        });
  
      } catch (parseError) {
        setError(`Error processing Excel file: ${parseError.message}`);
        setLoading(false);
      }
    };
    reader.onerror = () => {
      setError('Failed to read the file. Please ensure it is a valid .xls or .xlsx file.');
      setLoading(false);
    };
    reader.readAsArrayBuffer(acceptedFiles[0]);
  };
  

  if (loading) {
    return (
      <Container>
        <div className="title">C4W Registration Upload</div>
        <LoadingContainer>
          <Spinner />
          <p style={{ marginTop: '20px' }}>Processing your file...</p>
        </LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <div className="title">C4W Registration Upload</div>
        <ErrorBox>{error}</ErrorBox>
        <HButton
          category="Primary"
          size="Medium"
          text="Try Again"
          onClick={() => setError(null)}
        />
      </Container>
    );
  }

  if (result) {
    return (
      <Container>
        <div className="title">C4W Registration Upload</div>
        <ResultBox>{result}</ResultBox>
        <div style={{ display: 'flex', marginTop: '20px', gap: '10px' }}>
          <HButton
            category="Primary"
            size="Medium"
            text="Register More"
            onClick={() => {
              setResult(null);
              setError(null);
            }}
          />
          <HButton
            category="Secondary"
            size="Medium"
            text="Return to Registrations"
            onClick={() => {
              window.location.href = '/adm/c4w/registration';
            }}
          />
        </div>
      </Container>
    );
  }

  return (
    <Dropzone onDrop={onDrop} accept=".xls,.xlsx" noClick={true}>
      {({ getRootProps, getInputProps, open }) => (
        <Container>
          <div className="title">C4W Registration Upload</div>
          <ContentBox>
            <DownloadTemplate>
              <HButton
                category="Tertiary"
                size="Medium"
                text={'Download registration template'}
                onClick={onClickDownloadTemplateExcel}
              />
            </DownloadTemplate>
            <RectangleBox {...getRootProps({ className: 'dropzone' })} onClick={open}>
              <input {...getInputProps()} />
              <p>Drag and drop C4W registration template file here or click to select</p>
            </RectangleBox>
            <CheckBox>
              <input
                type="checkbox"
                id="overwriteRegistrationCheckbox"
                checked={checked}
                onChange={() => setChecked(!checked)}
              />
              <label htmlFor="overwriteRegistrationCheckbox">
                Do you want to overwrite registration information?
              </label>
            </CheckBox>
          </ContentBox>
        </Container>
      )}
    </Dropzone>
  );
};

export default C4WRegistrationUpload;